import React, { createContext, useState, useEffect, useContext } from "react";
import api from "../services/api";
import { jwtDecode } from "jwt-decode";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [condition, setCondition] = useState(null);
  const [caseNumber, setCaseNumber] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // Admin tokens are handled separately - admin components read from localStorage directly
      // This context only handles regular user authentication
      const storedToken = sessionStorage.getItem("token");
      const storedCondition = sessionStorage.getItem("condition");

      if (storedToken) {
        try {
          const decoded = jwtDecode(storedToken);
          if (decoded.exp * 1000 > Date.now()) {
            setUser(decoded);
            setToken(storedToken);
            setCondition(storedCondition);
            api.defaults.headers.common[
              "Authorization"
            ] = `Bearer ${storedToken}`;
            await startSession(); // Start session on initial auth
          } else {
            // Token expired — attempt silent reauth if we have study session info
            const session = JSON.parse(sessionStorage.getItem("spiders_session") || "{}");
            const studySession = Object.keys(sessionStorage)
              .filter((k) => k.startsWith("spiders_study_"))
              .map((k) => { try { return JSON.parse(sessionStorage.getItem(k)); } catch (e) { return null; } })
              .find((s) => s?.instanceId);

            const instId = session.instanceId || studySession?.instanceId;
            const pid = session.participantId || decoded.username;
            const sid = session.studyId || studySession?.studyId;

            if (instId && pid && sid) {
              try {
                const { data } = await api.post(`/studies/${sid}/reauth`, {
                  participantId: pid,
                  instanceId: instId,
                });
                if (data.token) {
                  const newDecoded = jwtDecode(data.token);
                  setUser(newDecoded);
                  setToken(data.token);
                  sessionStorage.setItem("token", data.token);
                  api.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
                  console.log("[Auth] Silent reauth successful");
                  await startSession();
                } else {
                  logout();
                }
              } catch (e) {
                console.warn("[Auth] Silent reauth failed:", e.message);
                logout();
              }
            } else {
              logout(); // No study context to reauth with
            }
          }
        } catch (error) {
          console.error("Error decoding token:", error);
          logout(); // On error, clear auth data
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const startSession = async () => {
    try {
      await api.startSession();
    } catch (error) {
      console.error("Error starting session:", error);
    }
  };

  const endSession = async () => {
    try {
      await api.endSession();
    } catch (error) {
      console.error("Error ending session:", error);
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post("/auth/register", userData);
      const { token, condition, caseNumber } = response.data;
      if (token) {
        setToken(token);
        setCondition(condition);
        setCaseNumber(caseNumber);
        const decoded = jwtDecode(token);
        setUser(decoded);

        sessionStorage.setItem("token", token);
        sessionStorage.setItem("condition", condition);
        sessionStorage.setItem("caseNumber", caseNumber);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        await startSession();

        return { success: true };
      } else {
        return {
          success: false,
          message: "Registration failed, no token received.",
        };
      }
    } catch (error) {
      console.error("Registration error:", error);
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "An error occurred during registration.",
      };
    }
  };

  const login = async (userData) => {
    try {
      const response = await api.post("/auth/login", userData);
      const { token, condition, caseNumber } = response.data;
      if (token) {
        setToken(token);
        setCondition(condition);
        setCaseNumber(caseNumber);
        const decoded = jwtDecode(token);
        setUser(decoded);

        sessionStorage.setItem("token", token);
        sessionStorage.setItem("condition", condition);
        sessionStorage.setItem("caseNumber", caseNumber);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        await api.startSession();
      } else {
        throw new Error("Failed to login, no token received.");
      }
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  // Set auth state directly (used after registration when we already have a token)
  const setAuthFromToken = async (tokenValue, userData) => {
    try {
      setToken(tokenValue);
      setCondition(userData.condition);
      setCaseNumber(userData.caseNumber);
      const decoded = jwtDecode(tokenValue);
      setUser(decoded);

      sessionStorage.setItem("token", tokenValue);
      if (userData.condition) sessionStorage.setItem("condition", userData.condition);
      if (userData.caseNumber) sessionStorage.setItem("caseNumber", userData.caseNumber);
      api.defaults.headers.common["Authorization"] = `Bearer ${tokenValue}`;

      await api.startSession();
    } catch (error) {
      console.error("Error setting auth from token:", error);
      throw error;
    }
  };

  const logout = async () => {
    // End user session
    try {
      await endSession();
    } catch (error) {
      console.error("Error ending session during logout:", error);
    }

    setUser(null);
    setToken(null);
    setCondition(null);
    setCaseNumber(null);
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("condition");
    sessionStorage.removeItem("caseNumber");
    // Do NOT remove admin_token - admin auth is separate (stays in localStorage)
    delete api.defaults.headers.common["Authorization"];
  };

  const adminLogout = () => {
    localStorage.removeItem("admin_token");
  };

  const adminLogin = async (userData) => {
    try {
      const response = await api.post("/admin/auth/login", userData);
      const { token } = response.data;
      if (token) {
        // Store admin token separately - admin components read it directly from localStorage
        localStorage.setItem("admin_token", token);
        // Do NOT set user/token state - keep admin auth separate from user auth
        // Do NOT clear regular user session - they can coexist
        // Admin pages will pass the token explicitly where needed.
      } else {
        throw new Error("Admin login failed, no token received.");
      }
    } catch (error) {
      console.error("Admin login error:", error);
      throw error;
    }
  };

  // Helper to check if admin is authenticated (reads directly from localStorage)
  const isAdminAuthenticated = () => {
    const adminToken = localStorage.getItem("admin_token");
    if (!adminToken) return false;
    try {
      const decoded = jwtDecode(adminToken);
      return decoded.exp * 1000 > Date.now() && decoded.isAdmin;
    } catch {
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        condition,
        caseNumber,
        loading,
        register,
        login,
        logout,
        adminLogin,
        adminLogout,
        isAdminAuthenticated,
        setAuthFromToken, // Set auth state directly after registration
        endSession, // Expose endSession for use in other components
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
